<script setup>
import { FlexRender } from '@tanstack/vue-table';
import SortButton from './SortButton.vue';
import { computed } from 'vue';

const props = defineProps({
  table: {
    type: Object,
    required: true,
  },
  fixed: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'relaxed',
  },
  /** `none` (default) flush; `soft` rounded bg + shadow, no border */
  panel: {
    type: String,
    default: 'none',
    validator: value => ['none', 'soft'].includes(value),
  },
  hoverableRows: {
    type: Boolean,
    default: true,
  },
});

const isRelaxed = computed(() => props.type === 'relaxed');

const headerCellPadding = computed(() =>
  isRelaxed.value ? 'py-3 px-5' : 'py-2 px-5'
);

const bodyCellPadding = computed(() =>
  isRelaxed.value ? 'py-4 px-5' : 'py-2 px-5'
);

const headerClass = computed(() =>
  isRelaxed.value
    ? 'ltr:first:rounded-bl-lg ltr:first:rounded-tl-lg ltr:last:rounded-br-lg ltr:last:rounded-tr-lg rtl:first:rounded-br-lg rtl:first:rounded-tr-lg rtl:last:rounded-bl-lg rtl:last:rounded-tl-lg'
    : ''
);

const outerPanelClass = computed(() =>
  props.panel === 'soft'
    ? 'overflow-hidden rounded-xl bg-n-solid-1 shadow-sm'
    : ''
);

const rowClass = computed(() =>
  props.hoverableRows
    ? 'transition-colors hover:bg-n-slate-2 dark:hover:bg-n-solid-3'
    : ''
);
</script>

<template>
  <div class="min-w-0" :class="outerPanelClass">
    <div class="overflow-x-auto">
      <table class="w-full" :class="{ 'table-fixed': fixed }">
        <thead class="sticky top-0 z-10 bg-n-slate-1">
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
            class="rounded-xl"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :style="{
                width: `${header.getSize()}px`,
              }"
              class="text-left text-sm font-medium text-n-slate-12"
              :class="[headerCellPadding, headerClass]"
              @click="
                header.column.getCanSort() && header.column.toggleSorting()
              "
            >
              <div
                v-if="!header.isPlaceholder"
                class="flex place-items-center gap-1"
              >
                <FlexRender
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
                <SortButton
                  v-if="header.column.getCanSort()"
                  :header="header"
                />
              </div>
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-n-slate-2">
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :class="rowClass"
          >
            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              :class="bodyCellPadding"
            >
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
